import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Star, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/onec_bases";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage, toastError } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";
import { useOneCBases } from "@/lib/use-onec-bases";
import { cn } from "@/lib/utils";

type FormState = {
  name: string;
  base_url: string;
  audience: string;
  is_default: boolean;
};

const EMPTY: FormState = { name: "", base_url: "", audience: "", is_default: false };

const REQUIRED: { key: "name" | "base_url" | "audience"; title: string }[] = [
  { key: "name", title: "Название" },
  { key: "base_url", title: "Адрес публикации" },
  { key: "audience", title: "Audience" },
];

/** Справочник баз 1С для SSO-входа (админ). В SSO-сессии портал ходит в 1С по
 *  JWT: `aud` токена — `audience` базы, адрес — `base_url`. Заводится один раз,
 *  общий для всех; пользователь выбирает базу у себя («Подключение 1С»), без
 *  выбора действует база по умолчанию — она у конфигурации ровно одна. */
export function OneCBasesAdminPage(): React.ReactElement {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useOneCBases();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());
  // Отказ сервера — строкой у формы, не только тостом (409 «такая база уже
  // есть», 422 по адресу с /odata/): тост легко пропустить.
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: api.ONEC_BASES_QUERY_KEY });
  const onFormError = (fallback: string) => (e: unknown) => {
    const msg = extractErrorMessage(e, fallback);
    setFormError(msg);
    toast.error(msg);
  };
  const reset = () => {
    setEditingId(null);
    setForm(EMPTY);
    setMissing(new Set());
    setFormError(null);
  };

  const createMut = useMutation({
    mutationFn: api.createOneCBase,
    onSuccess: () => {
      void invalidate();
      reset();
      toast.success("База добавлена");
    },
    onError: onFormError("Ошибка добавления базы"),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: api.OneCBaseUpdate }) =>
      api.updateOneCBase(args.id, args.body),
    onSuccess: () => {
      void invalidate();
      reset();
      toast.success("База сохранена");
    },
    onError: onFormError("Ошибка сохранения базы"),
  });

  // «Сделать по умолчанию» из списка — отдельной мутацией: её отказ не должен
  // писаться в форму, которая в этот момент может редактировать другую базу.
  const defaultMut = useMutation({
    mutationFn: (id: string) => api.updateOneCBase(id, { is_default: true }),
    onSuccess: () => {
      void invalidate();
      toast.success("База по умолчанию назначена");
    },
    onError: toastError("Не удалось назначить базу по умолчанию"),
  });

  const deleteMut = useMutation({
    mutationFn: api.deleteOneCBase,
    onSuccess: (_res, id) => {
      void invalidate();
      if (id === editingId) reset();
      toast.success("База удалена");
    },
    onError: toastError("Ошибка удаления базы"),
  });

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (missing.has(key)) {
      setMissing((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const editing = editingId ? data?.bases.find((b) => b.id === editingId) : undefined;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const empty = new Set(REQUIRED.filter((f) => !form[f.key].trim()).map((f) => f.key));
    setMissing(empty);
    if (empty.size > 0) {
      setFormError(
        "Заполните обязательные поля: " +
          REQUIRED.filter((f) => empty.has(f.key))
            .map((f) => f.title)
            .join(", "),
      );
      return;
    }
    setFormError(null);
    const fields = {
      name: form.name.trim(),
      base_url: form.base_url.trim(),
      audience: form.audience.trim(),
    };
    if (editingId && editing) {
      const body: api.OneCBaseUpdate = { ...fields };
      // Отметку шлём только при изменении: снять её с базы по умолчанию
      // бэкенд не даст (409 — «назначьте другую»), и это надо увидеть.
      if (form.is_default !== editing.is_default) body.is_default = form.is_default;
      updateMut.mutate({ id: editingId, body });
    } else {
      createMut.mutate({ ...fields, config_type: "unf", is_default: form.is_default });
    }
  };

  const startEdit = (b: api.OneCBase) => {
    setEditingId(b.id);
    setForm({
      name: b.name,
      base_url: b.base_url,
      audience: b.audience,
      is_default: b.is_default,
    });
    setMissing(new Set());
    setFormError(null);
  };

  const busy = createMut.isPending || updateMut.isPending;
  const errClass = (k: string) =>
    cn(missing.has(k) && "border-destructive focus-visible:ring-destructive");

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Базы 1С для входа через SSO</h1>
        <p className="text-sm text-muted-foreground">
          При входе через SSO портал ходит в 1С по токену (JWT) под учётной записью человека — в
          базу из этого справочника. Адрес — публикация без{" "}
          <code className="rounded bg-muted px-1">/odata/…</code> и{" "}
          <code className="rounded bg-muted px-1">/hs/…</code>; audience — ровно как в{" "}
          <code className="rounded bg-muted px-1">default.vrd</code> публикации.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {editingId ? `Редактирование: ${editing?.name ?? ""}` : "Новая база"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <div className="space-y-2">
              <Label htmlFor="onec-base-name">Название *</Label>
              <Input
                id="onec-base-name"
                placeholder="УНФ основная"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className={errClass("name")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="onec-base-url">Адрес публикации *</Label>
              <Input
                id="onec-base-url"
                placeholder="https://1c.example.com/unf"
                value={form.base_url}
                onChange={(e) => setField("base_url", e.target.value)}
                className={errClass("base_url")}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="onec-base-audience">Audience *</Label>
                <Input
                  id="onec-base-audience"
                  autoComplete="off"
                  value={form.audience}
                  onChange={(e) => setField("audience", e.target.value)}
                  className={errClass("audience")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="onec-base-config">Конфигурация</Label>
                {/* Пока бэкенд принимает только УНФ (`ConfigType`), и сменить
                    конфигурацию у заведённой базы нельзя. */}
                <select
                  id="onec-base-config"
                  value="unf"
                  disabled
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-70"
                >
                  <option value="unf">{api.CONFIG_TYPE_TITLES.unf}</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => setField("is_default", e.target.checked)}
              />
              По умолчанию (для тех, кто базу не выбирал)
            </label>
            {formError && (
              <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                {editingId ? "Сохранить" : "Добавить"}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={reset} disabled={busy}>
                  Отмена
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Справочник</h2>
        {(isLoading || error || !data) && (
          <LoadState
            isLoading={isLoading}
            error={error}
            onRetry={() => void refetch()}
            className="flex flex-col items-center gap-3 p-4 text-center"
          />
        )}
        {data && data.bases.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Баз ещё нет. Пока справочник пуст, в SSO-сессии 1С недоступна.
          </p>
        )}
        <ul className="space-y-2">
          {data?.bases.map((b) => (
            <li
              key={b.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  {b.name}
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {api.CONFIG_TYPE_TITLES[b.config_type] ?? b.config_type}
                  </span>
                  {b.is_default && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      по умолчанию
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">{b.base_url}</div>
                <div className="truncate text-xs text-muted-foreground">aud: {b.audience}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!b.is_default && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={defaultMut.isPending}
                    onClick={() => defaultMut.mutate(b.id)}
                  >
                    <Star className="size-4" /> По умолчанию
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Редактировать ${b.name}`}
                  onClick={() => startEdit(b)}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Удалить ${b.name}`}
                  disabled={deleteMut.isPending}
                  onClick={() => {
                    if (window.confirm(`Удалить базу «${b.name}» из справочника?`)) {
                      deleteMut.mutate(b.id);
                    }
                  }}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
